import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight, Youtube } from 'lucide-react';

// YouTube Shorts from Dr. Thamarai's Dental Care channel
const youtubeShorts = [
  {
    id: 'short1',
    videoId: 'xk1vbOV9p2s',
    title: 'Dental Care Tips',
  },
  {
    id: 'short2',
    videoId: 'YPu3iO29HgM',
    title: 'Clear Aligners Explained',
  },
  {
    id: 'short3',
    videoId: 'FYrgkJU72to',
    title: 'Teeth Whitening Process',
  },
  {
    id: 'short4',
    videoId: 'cfMmXqqYvs4',
    title: 'Oral Hygiene Tips',
  },
  {
    id: 'short5',
    videoId: '70aTZ1kAszg',
    title: 'Smile Correction',
  },
  {
    id: 'short6',
    videoId: '3AY5gXZMues',
    title: 'Preventive Dental Care',
  }
];

const YouTubeShortsSection = () => {
  const carouselRef = useRef(null);

  const scroll = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 280;
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="youtube-shorts-section">
      {/* Schema Markup for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "VideoGallery",
          "name": "Dental Care Videos by Dr. Thamarai's Dental Care",
          "description": "Educational dental care videos from Dr. Thamarai's Dental Care, East Tambaram, Chennai",
          "provider": {
            "@type": "Dentist",
            "name": "Dr. Thamarai's Dental Care",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "11/5, Albertponnuswamy street, Bharat Mata Road, Gandhi Nagar",
              "addressLocality": "Tambaram",
              "addressRegion": "Chennai, Tamil Nadu",
              "postalCode": "600059",
              "addressCountry": "IN"
            }
          }
        })}
      </script>

      <div className="youtube-shorts-container">
        {/* Header Section */}
        <div className="youtube-shorts-header">
          <div className="youtube-shorts-badge">
            <Youtube size={20} />
            <span>YouTube Shorts</span>
          </div>
          <h2 className="youtube-shorts-title">
            Videos by Dr. Thamarai's Dental Care
          </h2>
          <p className="youtube-shorts-description">
            Watch short, expert-led dental care videos from Dr. Thamarai's Dental Care, a trusted dental clinic in East Tambaram, Chennai. These YouTube Shorts explain common dental treatments, oral hygiene tips, invisible aligners, teeth cleaning, smile correction, and preventive dental care—directly from experienced dentists serving Tambaram and nearby areas.
          </p>
        </div>

        {/* Carousel Section */}
        <div className="youtube-shorts-carousel-wrapper">
          <button 
            className="carousel-nav-btn carousel-nav-left"
            onClick={() => scroll('left')}
            aria-label="Scroll left"
          >
            <ChevronLeft size={24} />
          </button>

          <div className="youtube-shorts-carousel" ref={carouselRef}>
            {youtubeShorts.map((short) => (
              <div key={short.id} className="youtube-short-card">
                <div className="youtube-short-embed">
                  <iframe
                    src={`https://www.youtube.com/embed/${short.videoId}?rel=0&modestbranding=1&playsinline=1&controls=1&enablejsapi=1`}
                    title={short.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            ))}
          </div>

          <button 
            className="carousel-nav-btn carousel-nav-right"
            onClick={() => scroll('right')}
            aria-label="Scroll right"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Local SEO Content */}
        <div className="youtube-shorts-seo-content">
          <div className="seo-content-grid">
            <div className="seo-content-block">
              <h3>Dental Videos for Chennai Patients</h3>
              <p>
                These short dental videos are designed to help patients from East Tambaram, Selaiyur, Medavakkam, Chromepet, Velachery, Perungalathur, and South Chennai understand modern dental treatments clearly before visiting a dentist.
              </p>
            </div>
            <div className="seo-content-block">
              <h3>Learn About Dental Treatments</h3>
              <p>
                Patients searching for a reliable dentist in East Tambaram or a dental clinic in Chennai can use these videos to learn about clear aligners, cosmetic dentistry, gum care, teeth whitening, routine dental checkups, and preventive oral health care. Each video answers common dental questions faced by patients in Chennai.
              </p>
            </div>
          </div>

          {/* Trust Footer */}
          <div className="youtube-shorts-footer">
            <div className="channel-badge">
              <Youtube size={18} />
              <span>Videos from our official YouTube channel – <strong>Dr. Thamarai's Dental Care</strong></span>
            </div>
            <a 
              href="https://www.youtube.com/@Dr.ThamaraisDentalCare/shorts" 
              target="_blank" 
              rel="noopener noreferrer"
              className="youtube-channel-link"
            >
              View All Videos
              <ChevronRight size={16} />
            </a>
          </div>

          {/* CTA */}
          <div className="youtube-shorts-cta">
            <p>Looking for a dentist near East Tambaram? Watch our videos and book a consultation.</p>
            <a href="tel:+917502297680" className="cta-button primary">
              Book Consultation
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default YouTubeShortsSection;
